/**
 * PreMeetChecklistPanel — list of saved pre-meet checklists with the
 * stepped PreMeetWizard as the entry point.
 *
 * The wizard handles all data capture (when, where, visibility,
 * compliance, review).  This panel is the at-a-glance recap +
 * lifecycle controls (delete, edit; future: send / acknowledge / met).
 */
import { useState } from "react";
import { BellRing, FileDown, Pencil, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  usePreMeetChecklists,
  type PreMeetChecklist,
} from "@/lib/safety/usePreMeet";
import {
  buildPreMeetChecklistPdf,
  downloadBlob,
  preMeetPdfFilename,
} from "@/lib/safety/preMeetPdf";
import { useTrustedShareNotifier } from "@/lib/safety/useTrustedShareNotifier";
import TrustedShareDecisionBadge from "@/components/safety/TrustedShareDecisionBadge";
import TrustedShareReceiptsPanel from "@/components/safety/TrustedShareReceiptsPanel";
import PreMeetWizard from "@/components/safety/PreMeetWizard";

const PreMeetChecklistPanel = () => {
  const { items, loading, remove, refresh } = usePreMeetChecklists();
  const { user } = useAuth();
  const { notify, contactCount } = useTrustedShareNotifier();
  const [editing, setEditing] = useState<PreMeetChecklist | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [resharingId, setResharingId] = useState<string | null>(null);

  const rearmReminders = async (id: string) => {
    const { error } = await supabase.rpc("fn_schedule_premeet_reminders", {
      _checklist_id: id,
    });
    if (error) toast.error(error.message);
    else toast.success("Reminders armed for 1 hour and 10 minutes before.");
  };

  const exportPdf = async (c: PreMeetChecklist) => {
    setExportingId(c.id);
    try {
      const ownerLabel =
        (user?.user_metadata?.full_name as string | undefined) ??
        user?.email ??
        "Wavlynq member";
      const blob = await buildPreMeetChecklistPdf(c, {
        ownerLabel,
        generatedAt: new Date(),
      });
      downloadBlob(blob, preMeetPdfFilename(c));
      toast.success("Checklist PDF downloaded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't build PDF.");
    } finally {
      setExportingId(null);
    }
  };

  const reshare = async (c: PreMeetChecklist) => {
    if (contactCount === 0) {
      toast.info("Add a Trusted Circle contact first.");
      return;
    }
    setResharingId(c.id);
    try {
      await notify(c);
    } finally {
      setResharingId(null);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Before any in-person meet, take 60 seconds. The wizard walks
        you through venue, visibility, and compliance — then shares
        with your Trusted Circle in one tap.
      </p>
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <BellRing className="h-3.5 w-3.5" />
        We'll remind you 1 hour and 10 minutes before each meet.
      </p>

      <PreMeetWizard onSaved={() => void refresh()} />

      {/* Controlled edit instance — opens when a row's pencil is tapped. */}
      <PreMeetWizard
        existing={editing}
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        onSaved={() => void refresh()}
        hideTrigger
      />

      {items.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Your checklists
          </p>
          {items.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-border bg-card p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{c.meet_place_label}</p>
                <p className="text-xs text-muted-foreground">
                  {c.meet_at ? new Date(c.meet_at).toLocaleString() : "No date set"} ·{" "}
                  {c.status}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                  {c.has_id_verified && (
                    <span className="rounded-full bg-success/10 text-success px-2 py-0.5">
                      ID
                    </span>
                  )}
                  {c.has_video_call_done && (
                    <span className="rounded-full bg-success/10 text-success px-2 py-0.5">
                      Video
                    </span>
                  )}
                  {c.has_safe_word && (
                    <span className="rounded-full bg-success/10 text-success px-2 py-0.5">
                      Safe word
                    </span>
                  )}
                  {c.has_transport && (
                    <span className="rounded-full bg-success/10 text-success px-2 py-0.5">
                      Transport
                    </span>
                  )}
                  {c.shared_with_trusted && (
                    <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5">
                      Shared
                    </span>
                  )}
                </div>
                <TrustedShareDecisionBadge decision={c.trusted_share_decision} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void exportPdf(c)}
                  disabled={exportingId === c.id}
                  aria-label="Download checklist PDF"
                  title="Download as PDF"
                >
                  <FileDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void reshare(c)}
                  disabled={resharingId === c.id || contactCount === 0}
                  aria-label="Re-share with Trusted Circle"
                  title={
                    contactCount === 0
                      ? "Add a Trusted Circle contact first"
                      : "Re-share with Trusted Circle"
                  }
                >
                  <Send className="h-4 w-4" />
                </Button>
                {c.meet_at && new Date(c.meet_at) > new Date() && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void rearmReminders(c.id)}
                    aria-label="Re-arm reminders"
                    title="Re-arm reminders (T-1h, T-10m)"
                  >
                    <BellRing className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditing(c)}
                  aria-label="Edit checklist"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(c.id)}
                  aria-label="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              </div>
              <TrustedShareReceiptsPanel checklist={c} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PreMeetChecklistPanel;
